import React, { useCallback, useMemo } from 'react';
import { Button, Checkbox } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import {
  AddRowButtonWrapper,
  CellInput,
  DataGridActionsCell,
  DataGridBody,
  DataGridCell,
  DataGridHeaderCell,
  DataGridRow,
  DataGridTable,
  DataGridWrapper,
} from './DataGridInput.styles';

export interface DataGridInputConfig {
  columns?: Array<{ key: string; label: string; type: string }>;
  minRows?: number | null;
  maxRows?: number | null;
  addRowLabel?: string | null;
  removeRowLabel?: string | null;
}

export interface DataGridInputProps {
  config: DataGridInputConfig;
  rows: Record<string, unknown>[];
  onChange: (rows: Record<string, unknown>[]) => void;
  'data-testid'?: string;
}

const emptyCellValue = (columnType: string): string | number | boolean => {
  switch (columnType) {
    case 'number':
      return 0;
    case 'checkbox':
      return false;
    default:
      return '';
  }
};

const createEmptyRow = (
  columns: Array<{ key: string; label: string; type: string }>,
): Record<string, unknown> =>
  columns.reduce<Record<string, unknown>>((acc, col) => {
    acc[col.key] = emptyCellValue(col.type);
    return acc;
  }, {});

export const DataGridInput: React.FC<DataGridInputProps> = ({
  config,
  rows,
  onChange,
  'data-testid': dataTestId,
}) => {
  const columns = useMemo(() => config.columns ?? [], [config.columns]);
  const minRows = config.minRows ?? 0;
  const maxRows = config.maxRows ?? 100;
  const addRowLabel = config.addRowLabel ?? 'Add row';
  const removeRowLabel = config.removeRowLabel ?? 'Remove row';

  const normalizedRows = useMemo(() => {
    const current = [...rows];
    while (current.length < minRows) {
      current.push(createEmptyRow(columns));
    }
    return current;
  }, [rows, minRows, columns]);

  const setRowCell = useCallback(
    (
      rowIndex: number,
      columnKey: string,
      cellValue: string | number | boolean,
    ) => {
      const next = normalizedRows.map((row, i) =>
        i === rowIndex ? { ...row, [columnKey]: cellValue } : row,
      );
      onChange(next);
    },
    [normalizedRows, onChange],
  );

  const addRow = useCallback(() => {
    if (normalizedRows.length >= maxRows) return;
    onChange([...normalizedRows, createEmptyRow(columns)]);
  }, [normalizedRows, maxRows, columns, onChange]);

  const removeRow = useCallback(
    (index: number) => {
      if (normalizedRows.length <= minRows) return;
      const next = normalizedRows.filter((_, i) => i !== index);
      onChange(next);
    },
    [normalizedRows, minRows, onChange],
  );

  const canAddRow = normalizedRows.length < maxRows;
  const canRemoveRow = normalizedRows.length > minRows;

  if (columns.length === 0) {
    return null;
  }

  return (
    <DataGridWrapper data-testid={dataTestId}>
      <DataGridTable>
        <thead>
          <tr>
            {columns.map((col) => (
              <DataGridHeaderCell key={col.key}>{col.label}</DataGridHeaderCell>
            ))}
            <DataGridHeaderCell />
          </tr>
        </thead>
        <DataGridBody>
          {normalizedRows.map((row, rowIndex) => (
            <DataGridRow
              key={rowIndex}
              data-testid={
                dataTestId ? `${dataTestId}-row-${rowIndex}` : undefined
              }
            >
              {columns.map((col) => (
                <DataGridCell key={col.key}>
                  {col.type === 'checkbox' ? (
                    <Checkbox
                      checked={Boolean(row[col.key])}
                      onChange={() =>
                        setRowCell(rowIndex, col.key, !row[col.key])
                      }
                      data-testid={
                        dataTestId
                          ? `${dataTestId}-cell-${rowIndex}-${col.key}`
                          : undefined
                      }
                    />
                  ) : (
                    <CellInput
                      type={
                        col.type === 'number'
                          ? 'number'
                          : col.type === 'date'
                            ? 'date'
                            : 'text'
                      }
                      value={
                        row[col.key] !== undefined && row[col.key] !== null
                          ? String(row[col.key])
                          : ''
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        const val =
                          col.type === 'number'
                            ? e.target.value === ''
                              ? 0
                              : Number(raw)
                            : col.type === 'date'
                              ? raw
                              : raw;
                        setRowCell(rowIndex, col.key, val);
                      }}
                      data-testid={
                        dataTestId
                          ? `${dataTestId}-cell-${rowIndex}-${col.key}`
                          : undefined
                      }
                    />
                  )}
                </DataGridCell>
              ))}
              <DataGridActionsCell>
                <Button
                  variant="tertiary"
                  onClick={() => removeRow(rowIndex)}
                  disabled={!canRemoveRow}
                  data-testid={
                    dataTestId
                      ? `${dataTestId}-remove-row-${rowIndex}`
                      : undefined
                  }
                >
                  <StyledIconInButton
                    className="uil uil-minus"
                    size="medium"
                    position="left"
                  />
                  {removeRowLabel}
                </Button>
              </DataGridActionsCell>
            </DataGridRow>
          ))}
        </DataGridBody>
      </DataGridTable>
      <AddRowButtonWrapper>
        <Button
          variant="secondary"
          onClick={addRow}
          disabled={!canAddRow}
          data-testid={dataTestId ? `${dataTestId}-add-row` : undefined}
        >
          <StyledIconInButton
            className="uil uil-plus"
            size="medium"
            position="left"
          />
          {addRowLabel}
        </Button>
      </AddRowButtonWrapper>
    </DataGridWrapper>
  );
};
